import React, { useState } from 'react';
import { Card, CardContent, CardHeader, ScrollArea, Tabs, TabsContent, TabsList, TabsTrigger, TooltipProvider, useToast, } from '@cargomatic/ui';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { ChevronUp, ChevronDown } from 'lucide-react';
import OrderRtbStatCards from 'components/modules/orders/order-rtb/OrderRtbStatCards';
import { useOrderRTBArLineItems, useOrderRTBApLineItems, } from 'components/modules/orders/order-rtb/hooks/useOrderRTBLineItems';
import AccountingReviewDate from 'components/modules/orders/order-rtb/AccountingReviewDate';
import OrderBillingStatus from 'components/modules/orders/order-rtb/OrderBillingStatus';
import ARChargesTable from 'components/modules/orders/order-rtb/ARChargesTable';
import APChargesTable from 'components/modules/orders/order-rtb/APChargesTable';
import AMCChargesTable from 'components/modules/orders/order-rtb/AMCChargesTable';
import { useOrderRtbActions } from 'components/modules/orders/order-rtb/hooks/useOrderRtbActions';
import AddOrAdjustDrayageAccessorialModal from 'components/modules/shipments/accounting/AddOrAdjustDrayageAccessorialModal';
import AddBaseCostDialog from 'components/modules/orders/order-rtb/AddBaseCostDialog';
export default function FinancialManagementPage({ orderId, order, shipments, }) {
    var _a, _b, _c, _d;
    const [isAccountingOpen, setIsAccountingOpen] = useState(true);
    // Toast hook for notifications
    const { toast } = useToast();
    // Rate cards via query hook
    const shipperIdFromOrder = ((_a = order === null || order === void 0 ? void 0 : order.shipper) === null || _a === void 0 ? void 0 : _a._id) && typeof order.shipper._id === 'string'
        ? order.shipper._id
        : undefined;
    const carrierIdFromOrder = ((_b = order === null || order === void 0 ? void 0 : order.carrier) === null || _b === void 0 ? void 0 : _b._id) && typeof order.carrier._id === 'string'
        ? order.carrier._id
        : undefined;
    // If order-level carrier is absent, attempt to infer a single carrier from shipments
    const carrierIdFromShipments = React.useMemo(() => {
        var _a;
        if (!shipments || !Array.isArray(shipments) || shipments.length === 0) {
            return undefined;
        }
        const ids = new Set();
        for (const s of shipments) {
            const id = ((_a = s === null || s === void 0 ? void 0 : s.carrier) === null || _a === void 0 ? void 0 : _a._id) && typeof s.carrier._id === 'string'
                ? s.carrier._id
                : undefined;
            if (id)
                ids.add(id);
            if (ids.size > 1)
                break;
        }
        return ids.size === 1 ? Array.from(ids)[0] : undefined;
    }, [shipments]);
    // Narrow billing status string from order to the union type expected by OrderBillingStatus
    const allowedBillingStatuses = [
        'awaiting_billable_event',
        'incomplete',
        'ready_to_bill',
        'audited',
        'invoiced',
        'supplemental_invoice',
        'template_billing',
        'customer_portal',
        'billing_hold',
        'pending_review',
    ];
    function isBillingStatus(value) {
        return allowedBillingStatuses.includes(value);
    }
    const billingStatusRaw = (_c = order === null || order === void 0 ? void 0 : order.order_billing_status_state) === null || _c === void 0 ? void 0 : _c.billing_status;
    const billingStatusFromOrder = billingStatusRaw &&
        typeof billingStatusRaw === 'string' &&
        isBillingStatus(billingStatusRaw)
        ? billingStatusRaw
        : undefined;
    // ids for child components
    const shipperId = shipperIdFromOrder;
    const carrierId = carrierIdFromOrder !== null && carrierIdFromOrder !== void 0 ? carrierIdFromOrder : carrierIdFromShipments;
    // Track active tab; only load the active tab's data
    const [activeTab, setActiveTab] = useState('ar');
    // Loading hooks for tables (only enabled for active tab)
    const { data: arLines = [], isLoading: _isLoadingAR, refetch: refetchArLines, } = useOrderRTBArLineItems(orderId, {
        enabled: activeTab === 'ar' && Boolean(orderId),
    });
    const { data: apLines = [], isLoading: _isLoadingAP, refetch: refetchApLines, } = useOrderRTBApLineItems(orderId, {
        enabled: activeTab === 'ap' && Boolean(orderId),
    });
    // Centralized actions hook for edit, adjustments, base cost
    const { activeAction, shipment: shipmentForEdit, isLoadingShipment: _isLoadingShipmentForEdit, editCharge: handleEditCharge, addBaseCost: handleAddBaseCost, clearAction, saveBaseCost: handleSaveBaseCost, baseCostValue, setBaseCostValue, isSavingBaseCost, orderNeedsBaseCost, isEditModalOpen, isBaseCostDialogOpen: showAddBaseCostDialog, setPendingRefetch, } = useOrderRtbActions({
        orderId,
        arLines,
        apLines,
        refetchArLines,
        refetchApLines,
        currency: order === null || order === void 0 ? void 0 : order.currency,
    });
    // Get equipment list from order for adjustment modal
    const equipmentList = React.useMemo(() => {
        if (!(order === null || order === void 0 ? void 0 : order.equipments))
            return [];
        return order.equipments.map((eq) => ({
            _id: eq._id,
            equipment_id: eq.equipment_id,
        }));
    }, [order === null || order === void 0 ? void 0 : order.equipments]);
    return (React.createElement(TooltipProvider, null,
        React.createElement(Card, null,
            React.createElement(CardHeader, { className: "flex flex-row items-center justify-between cursor-pointer", onClick: () => setIsAccountingOpen(!isAccountingOpen) },
                React.createElement(ScrollArea, { className: "w-full" },
                    React.createElement("div", { className: "flex items-center justify-between gap-4 pb-2" },
                        React.createElement("div", { className: "flex items-center gap-2 whitespace-nowrap" },
                            isAccountingOpen ? (React.createElement(ChevronUp, { className: "h-5 w-5 flex-shrink-0" })) : (React.createElement(ChevronDown, { className: "h-5 w-5 flex-shrink-0" })),
                            React.createElement("h1", { className: "text-xl font-semibold" }, "Accounting")),
                        React.createElement("div", { className: "flex items-center gap-4 flex-shrink-0" },
                            React.createElement(AccountingReviewDate, { orderId: orderId, accountingReviewDate: order === null || order === void 0 ? void 0 : order.accounting_review_date }),
                            React.createElement(OrderBillingStatus, { orderId: orderId, billingStatus: billingStatusFromOrder }))))),
            isAccountingOpen && (React.createElement(CardContent, { className: "space-y-6" },
                React.createElement("div", { className: "space-y-4" },
                    React.createElement(OrderRtbStatCards, { orderId: orderId })),
                React.createElement(Tabs, { defaultValue: "ar", className: "w-full", onValueChange: (value) => setActiveTab(value) },
                    React.createElement(TabsList, { className: "grid w-full grid-cols-3" },
                        React.createElement(TabsTrigger, { value: "ar" }, "Accounts Receivable"),
                        React.createElement(TabsTrigger, { value: "ap" }, "Accounts Payable"),
                        React.createElement(TabsTrigger, { value: "amc" }, "Accessorial Management Center")),
                    React.createElement(TabsContent, { value: "amc", className: "mt-6" },
                        React.createElement(AMCChargesTable, { orderId: orderId })),
                    React.createElement(TabsContent, { value: "ar", className: "mt-6" },
                        React.createElement(ARChargesTable, { orderId: orderId, shipperId: shipperId, onEditCharge: (charge) => handleEditCharge(charge, 'ar'), groupLikeInvoiceItems: (_d = order === null || order === void 0 ? void 0 : order.shipper) === null || _d === void 0 ? void 0 : _d.group_like_invoice_items, onAddBaseCost: () => handleAddBaseCost('ar'), showAddBaseCost: orderNeedsBaseCost('ar') })),
                    React.createElement(TabsContent, { value: "ap", className: "mt-6" },
                        React.createElement(APChargesTable, { orderId: orderId, carrierId: carrierId, onEditCharge: (charge) => handleEditCharge(charge, 'ap') })))))),
        isEditModalOpen &&
            activeAction &&
            'charge' in activeAction &&
            shipmentForEdit &&
            (() => {
                // Find the line item in the fetched shipment
                const ledger = activeAction.ledger === 'receivables'
                    ? shipmentForEdit.receivables
                    : shipmentForEdit.payables;
                // Try to find by _id first, then by adjustment_id, then by charge_id, then by value+message
                let lineItem = ledger === null || ledger === void 0 ? void 0 : ledger.find((item) => item._id === activeAction.charge._id);
                if (!lineItem && activeAction.charge.adjustment_id) {
                    lineItem = ledger === null || ledger === void 0 ? void 0 : ledger.find((item) => item._id === activeAction.charge.adjustment_id);
                }
                if (!lineItem && activeAction.charge.charge_id) {
                    lineItem = ledger === null || ledger === void 0 ? void 0 : ledger.find((item) => item.charge_id === activeAction.charge.charge_id);
                }
                if (!lineItem) {
                    lineItem = ledger === null || ledger === void 0 ? void 0 : ledger.find((item) => item.value === activeAction.charge.value &&
                        (item.message === activeAction.charge.message ||
                            item.type === activeAction.charge.type));
                }
                if (!lineItem) {
                    return null;
                }
                // Determine the type for the modal's cost display logic
                // For AR (receivables), use 'shipper_cost' or keep 'base_cost' if that's the category
                // For AP (payables), use 'carrier_cost' or keep 'base_cost'
                let displayType = lineItem.type;
                if (lineItem.category === 'base_cost') {
                    displayType = 'base_cost';
                }
                else if (activeAction.ledger === 'receivables') {
                    displayType = 'shipper_cost';
                }
                else {
                    displayType = 'carrier_cost';
                }
                const customAdjustment = Object.assign(Object.assign({}, lineItem), { type: displayType, ledger: activeAction.ledger, total: lineItem.value });
                // Handler for onSave - receives { ledger, adjustment } format
                const handleAdjustmentSave = (adjustments) => {
                    // Set pending refetch to trigger when ShipmentStore emits change
                    setPendingRefetch(activeAction.ledger);
                    adjustments.forEach((data) => {
                        ShipmentsActionCreators.addAdjustment(shipmentForEdit, data.ledger, data.adjustment);
                    });
                    clearAction();
                };
                // Handler for onEditAdjustment - receives flat { _id, ledger, value, ... } format
                const handleEditAdjustment = async (editedAdjustments) => {
                    try {
                        const adjustmentIds = editedAdjustments.map((a) => a._id);
                        const { ledger } = editedAdjustments[0];
                        const shipmentLedger = shipmentForEdit[ledger];
                        if (!shipmentLedger)
                            return;
                        const modifiedAdjustments = shipmentLedger
                            .filter((a) => adjustmentIds.includes(a._id))
                            .map((modifiedAdjustment) => {
                            const adjustment = editedAdjustments.find((a) => a._id === modifiedAdjustment._id);
                            if (!adjustment)
                                return modifiedAdjustment;
                            const updated = Object.assign({}, modifiedAdjustment);
                            Object.keys(adjustment).forEach((key) => {
                                if (key === 'value') {
                                    updated[key] = parseFloat(String(adjustment[key]));
                                }
                                else {
                                    updated[key] = adjustment[key];
                                }
                            });
                            return updated;
                        });
                        if (!modifiedAdjustments.length)
                            return;
                        await ShipmentWebAPIUtils.updateShipmentAdjustments(shipmentForEdit, ledger, modifiedAdjustments);
                        // Directly refetch the table data
                        if (activeAction.ledger === 'receivables') {
                            refetchArLines();
                        }
                        else {
                            refetchApLines();
                        }
                        clearAction();
                    }
                    catch (e) {
                        console.error('Error updating adjustments:', e);
                        toast({
                            title: 'Error',
                            description: 'Failed to update adjustment. Please try again.',
                            variant: 'destructive',
                        });
                    }
                };
                return (React.createElement(AddOrAdjustDrayageAccessorialModal, { shipment: shipmentForEdit, show: true, customAdjustment: customAdjustment, equipment: equipmentList, onSave: handleAdjustmentSave, onEditAdjustment: handleEditAdjustment, onHide: clearAction }));
            })(),
        React.createElement(AddBaseCostDialog, { open: showAddBaseCostDialog, ledger: activeAction === null || activeAction === void 0 ? void 0 : activeAction.ledger, baseCostValue: baseCostValue, onBaseCostValueChange: setBaseCostValue, onSave: handleSaveBaseCost, onClose: clearAction, isSaving: isSavingBaseCost })));
}
